import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Ship,
  FileCheck,
  Package,
  Users,
  Globe,
  Truck,
} from "lucide-react";

const IMAGES = {
  logistics: "https://images.unsplash.com/photo-1703977883249-d959f2b0c1ae?crop=entropy&cs=srgb&fm=jpg&q=85",
};

const capabilities = [
  {
    icon: Ship,
    title: "Export-Oriented Supply",
    description:
      "Purpose-built supply chain designed for international shipments with reliable lead times and consistent quality.",
  },
  {
    icon: Package,
    title: "Bulk Order Fulfillment",
    description:
      "Scalable capacity to handle large volume orders while maintaining product quality and delivery schedules.",
  },
  {
    icon: Users,
    title: "Sourcing Network",
    description:
      "Established relationships with certified production partners across India ensuring consistent raw material supply.",
  },
  {
    icon: FileCheck,
    title: "Compliance-Ready",
    description:
      "All products meet international food safety standards with proper documentation for customs clearance.",
  },
  {
    icon: Truck,
    title: "Logistics Coordination",
    description:
      "End-to-end logistics support from production facility to your destination port or warehouse.",
  },
  {
    icon: Globe,
    title: "Global Reach",
    description:
      "Experience shipping to markets in Europe, Middle East, Southeast Asia, and the Americas.",
  },
];

const certifications = [
  "FDA Compliant",
  "EU Food Contact",
  "BPI Certified",
  "SGS Tested",
  "ISO 22000",
  "HACCP Ready",
];

const ExportPage = () => {
  return (
    <div data-testid="export-page" className="noise-overlay">
      <Helmet>
        <title>Global Export | Biodegradable Packaging Exporter from India</title>
        <meta name="description" content="Prithira Global is an export-ready supplier of bagasse tableware, areca leaf plates, and biodegradable bags. Compliant documentation, FOB/CIF shipping, bulk MOQ." />
        <meta name="keywords" content="biodegradable packaging exporter India, bagasse exporter, areca leaf plates exporter, eco friendly packaging FOB CIF, India sustainable packaging supplier" />
        <link rel="canonical" href="https://www.prithiraglobal.com/export" />
        <meta property="og:title" content="Global Export | Prithira Global" />
        <meta property="og:description" content="Export-ready biodegradable packaging from India — bagasse, areca leaf, bio bags. Worldwide shipping with full documentation." />
        <meta property="og:url" content="https://www.prithiraglobal.com/export" />
      </Helmet>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-sand-200/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <span className="caption text-primary mb-4 block">Global Supply</span>
            <h1 className="heading-xl text-stone-900 mb-8">
              Export-Ready <span className="italic">Solutions</span>
            </h1>
            <p className="body-lg text-stone-600 max-w-3xl mx-auto">
              From Indian production facilities to global markets — reliable,
              compliant, and sustainable supply for international buyers.
            </p>
            <p
              data-testid="export-iec-line"
              className="mt-8 mx-auto max-w-3xl text-stone-700 text-sm md:text-base font-light leading-relaxed border-t border-stone-300/60 pt-6"
            >
              Prithira Global is a registered Indian exporter holding{" "}
              <span className="font-medium text-stone-900">IEC No. AQAPR7398L</span>,
              issued by the Directorate General of Foreign Trade, Government of India.
            </p>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="bg-white pb-20">
        <div className="container-custom">
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src={IMAGES.logistics}
              alt="Export logistics"
              className="w-full aspect-[4/5] sm:aspect-[16/10] md:aspect-[21/9] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t sm:bg-gradient-to-r from-stone-900/85 via-stone-900/55 to-stone-900/20 sm:to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 md:bottom-12 md:left-12 md:right-auto md:max-w-lg">
              <h2 className="font-heading text-xl sm:text-2xl md:text-3xl text-white mb-2 md:mb-3 leading-snug">
                Trusted by Importers Worldwide
              </h2>
              <p className="text-white/90 text-xs sm:text-sm font-light leading-relaxed">
                Our export operations ensure your sustainable packaging arrives
                on time, every time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption text-primary mb-4 block">Capabilities</span>
            <h2 className="heading-lg text-stone-900 mb-6">
              Our Export <span className="italic">Strengths</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((cap, index) => (
              <div
                key={index}
                data-testid={`capability-${index}`}
                className="bg-sand-200/30 rounded-2xl p-8 hover:bg-sand-200/50 transition-colors"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                  <cap.icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl text-stone-900 mb-3">
                  {cap.title}
                </h3>
                <p className="text-stone-600 font-light text-sm leading-relaxed">
                  {cap.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section-spacing bg-sand-200/30">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="caption text-primary mb-4 block">Standards</span>
            <h2 className="heading-lg text-stone-900 mb-6">
              Certifications & <span className="italic">Compliance</span>
            </h2>
            <p className="body-base text-stone-600 max-w-2xl mx-auto">
              Our products and processes meet international standards for food
              safety and environmental compliance.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="bg-white border border-stone-200 px-6 py-4 rounded-xl flex items-center gap-3"
              >
                <FileCheck className="h-5 w-5 text-primary" strokeWidth={1.5} />
                <span className="text-sm font-medium text-stone-700">{cert}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption text-primary mb-4 block">Process</span>
            <h2 className="heading-lg text-stone-900 mb-6">
              How We <span className="italic">Work</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: "01",
                title: "Inquiry",
                desc: "Share your requirements, volumes, and destination details.",
              },
              {
                step: "02",
                title: "Quote",
                desc: "Receive detailed pricing including shipping and documentation.",
              },
              {
                step: "03",
                title: "Production",
                desc: "We coordinate sourcing with our certified partners.",
              },
              {
                step: "04",
                title: "Delivery",
                desc: "Complete logistics coordination to your destination.",
              },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-heading text-primary/20 mb-4">
                  {item.step}
                </div>
                <h3 className="font-heading text-lg text-stone-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-stone-600 font-light text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-stone-900 text-white">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-lg mb-6">
              Ready to Discuss Your <span className="italic">Requirements</span>?
            </h2>
            <p className="text-stone-400 font-light leading-relaxed mb-8">
              Our export team is ready to provide detailed information about
              products, pricing, minimum order quantities, and shipping options.
            </p>
            <Link to="/partner">
              <Button
                data-testid="export-cta-btn"
                className="rounded-full px-8 py-6 text-base font-medium bg-white text-stone-900 hover:bg-stone-100 btn-lift"
              >
                Request Export Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ExportPage;
